import * as React from "react";
import CommonInput from "../input";
import EmojiPicker, { EmojiStyle } from "emoji-picker-react";
import { makeStyles } from "@material-ui/core";
import CommonButton from "../button";

const PUSH_NOTIFICATION_LIMITS = {
    title: 50,
    body: 150
};

const getCharacterCount = (value = "") => Array.from(value).length;

const truncateToLimit = (value = "", maxCharacters = 0) => {
    if (!maxCharacters) return value;
    return Array.from(value).slice(0, maxCharacters).join("");
};

const useStyles = makeStyles(() => ({
    form: {
        width: "100%",
        position: "relative"
    },
    fieldWrapper: {
        width: "100%",
        position: "relative"
    },
    inputField: {
        width: "100%"
    },
    inlineEmojiButton: {
        position: "absolute",
        right: "12px",
        top: "45px",
        cursor: "pointer",
        fontSize: "1.2rem",
        zIndex: 5,
        lineHeight: 1
    },
    emojiWrapper: {
        position: "absolute",
        right: 0,
        top: "calc(100% - 8px)",
        zIndex: 20,
        "@media (max-width: 768px)": {
            right: "50%",
            transform: "translateX(50%)"
        }
    },
    bodyGrp: {
        width: "100%"
    }
}));

export default function SendOutNotificationForm({
    handleSubmit,
    form,
    setForm
}) {
    const classes = useStyles();
    const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
    const [emojiField, setEmojiField] = React.useState("");
    const [focusedField, setFocusedField] = React.useState("");

    const textareaRef = React.useRef(null);
    const titleRef = React.useRef(null);

    const insertEmoji = (fieldRef, value, emoji, setValue, limit) => {
        const input = fieldRef.current;
        if (!input) return;
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const newText = truncateToLimit(
            value.substring(0, start) + emoji + value.substring(end, value.length),
            limit
        );
        setValue(newText);

        setTimeout(() => {
            input.focus();
            const nextCursorPosition = Math.min(start + Array.from(emoji).length, getCharacterCount(newText));
            input.selectionStart = input.selectionEnd = nextCursorPosition;
        }, 0);
    };
    const handleEmojiClick = (emojiData, field) => {
        const emoji = emojiData.emoji;
        if (field === "title") {
            insertEmoji(
                titleRef,
                form.title || "",
                emoji,
                (newTitle) => setForm((s) => ({ ...s, title: newTitle })),
                PUSH_NOTIFICATION_LIMITS.title
            );
        } else {
            insertEmoji(
                textareaRef,
                form.body || "",
                emoji,
                (newBody) => setForm((s) => ({ ...s, body: newBody })),
                PUSH_NOTIFICATION_LIMITS.body
            );
        }
    };

    const titleLength = getCharacterCount(form?.title || "");
    const bodyLength = getCharacterCount(form?.body || "");
    const toggleEmojiPicker = (field) => {
        setEmojiField(field);
        setShowEmojiPicker((prev) => (emojiField === field ? !prev : true));
    };

    return (
        <div>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    // High-sensitivity: this fans a push + in-app notification to
                    // EVERY staff user. Require an explicit confirmation first.
                    if (
                        window.confirm(
                            "This sends a push and in-app notification to ALL staff. Continue?"
                        )
                    ) {
                        handleSubmit(e);
                    }
                }}
                action="#"
                noValidate
                className={classes.form}
            >
                <div className={classes.fieldWrapper}>
                    <CommonInput
                        label={"Title"}
                        value={form?.title}
                        placeholder={"Enter Notification Title"}
                        onChange={(e) => {
                            const nextTitle = truncateToLimit(e.target.value, PUSH_NOTIFICATION_LIMITS.title);
                            setForm((s) => ({ ...s, title: nextTitle }));
                            setEmojiField("title");
                        }}
                        helperText={`${titleLength}/${PUSH_NOTIFICATION_LIMITS.title} characters`}
                        inputRef={titleRef}
                        onFocus={() => {
                            setEmojiField("title");
                            setFocusedField("title");
                        }}
                        onBlur={() => {
                            if (!showEmojiPicker) {
                                setFocusedField("");
                            }
                        }}
                        onClick={() => {
                            setShowEmojiPicker(false);
                        }}
                        className={classes.inputField}
                    />
                    {focusedField === "title" && (
                        <span
                            className={classes.inlineEmojiButton}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => toggleEmojiPicker("title")}
                        >
                            😊
                        </span>
                    )}
                    {showEmojiPicker && emojiField === "title" && (
                        <div className={classes.emojiWrapper}>
                            <EmojiPicker
                                onEmojiClick={(ed) => handleEmojiClick(ed, "title")}
                                emojiStyle={EmojiStyle.APPLE}
                            />
                        </div>
                    )}
                </div>
                <div className={classes.bodyGrp}>
                    <div className={classes.fieldWrapper}>
                        <CommonInput
                            inputRef={textareaRef}
                            label={"Body"}
                            fullWidth
                            value={form?.body}
                            multiline
                            placeholder={"Enter Notification Text"}
                            onChange={(e) => {
                                const nextBody = truncateToLimit(e.target.value, PUSH_NOTIFICATION_LIMITS.body);
                                setForm((s) => ({ ...s, body: nextBody }));
                                setEmojiField("body");
                            }}
                            helperText={`${bodyLength}/${PUSH_NOTIFICATION_LIMITS.body} characters`}
                            onFocus={() => {
                                setEmojiField("body");
                                setFocusedField("body");
                            }}
                            onBlur={() => {
                                if (!showEmojiPicker) {
                                    setFocusedField("");
                                }
                            }}
                            onClick={() => {
                                setShowEmojiPicker(false);
                            }}
                            className={classes.inputField}
                        />
                        {focusedField === "body" && (
                            <span
                                className={classes.inlineEmojiButton}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => toggleEmojiPicker("body")}
                            >
                                😊
                            </span>
                        )}
                        {showEmojiPicker && emojiField === "body" && (
                            <div className={classes.emojiWrapper}>
                                <EmojiPicker
                                    onEmojiClick={(ed) => handleEmojiClick(ed, "body")}
                                    emojiStyle={EmojiStyle.APPLE}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <CommonButton
                    disabled={titleLength < 3 || bodyLength < 6}
                    type="submit"
                    stylevariant="primary"
                    text={"Push"}
                />
            </form>
        </div>
    );
}
